import { Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ts, ts2 } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { CategoryIcon, StatusIndicator } from 'src/pages/PlannerPage/RoomRecognition/checklistIcons';
import { ChecklistItemStatus, ChecklistKey, CHECKLIST_ORDER, ChecklistState } from 'src/pages/PlannerPage/RoomRecognition/recognitionTypes';

interface RoomRecognitionProgressModalProps {
    checklist: ChecklistState;
    monthlyLimit: number;
}

export function RoomRecognitionProgressModal({ checklist, monthlyLimit }: RoomRecognitionProgressModalProps) {
    const itemLabels: Record<ChecklistKey, string> = {
        [ChecklistKey.MEASUREMENT]: ts('room_recognition_measurement', 'Measurement'),
        [ChecklistKey.OUTER_WALL]: ts('room_recognition_outer_wall', 'Outer wall'),
        [ChecklistKey.ROOMS]: ts('room_recognition_rooms', 'Rooms'),
        [ChecklistKey.DOORS]: ts('room_recognition_doors', 'Doors'),
        [ChecklistKey.WINDOWS]: ts('room_recognition_windows', 'Windows'),
    };

    return (
        <BaseDialog
            onClose={() => {}}
            title={ts('room_recognition_in_progress', 'Room recognition in progress...')}
            subtitle={ts('room_recognition_analyzing', "We're analyzing your floor plan to detect the following:")}
            titleColor={theme.palette.primary.main}
            disableClose
            hideClose
            maxWidth="sm"
        >
            <Stack spacing={1.25} alignItems="flex-start" sx={{ mt: 3, mx: 'auto', width: 'fit-content' }}>
                {CHECKLIST_ORDER.map((key) => {
                    const status = checklist[key];
                    const isSuccess = status === ChecklistItemStatus.SUCCESS;
                    const color = isSuccess ? theme.palette.primary.main : theme.palette.text.secondary;
                    return (
                        <Stack key={key} direction="row" alignItems="center" spacing={1.25}>
                            <Stack alignItems="center" justifyContent="center" sx={{ width: 38, minWidth: 38, height: 38 }}>
                                <StatusIndicator status={status} />
                            </Stack>
                            <Stack alignItems="center" sx={{ width: 30, minWidth: 30, color }}>
                                <CategoryIcon itemKey={key} />
                            </Stack>
                            <Typography sx={{ fontWeight: 500, fontSize: 16, color }}>
                                {itemLabels[key]}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>

            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 3 }}>
                <Typography sx={{ fontSize: 16 }}>
                    {ts('room_recognition_almost_ready', 'Almost ready - preparing your layout.')}
                </Typography>
                <Tooltip title={
                    ts2(
                        'room_recognition_limit_hint',
                        'Track up to {limit} floor plans each month.', { limit: monthlyLimit.toString() }
                    )
                }>
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </Tooltip>
            </Stack>
        </BaseDialog>
    );
}
